import { useFetchPage } from "~/core/composables/useFetchPage";
import { SearchMode, useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
} from "~/core/models/filters";
import type { AsyncLog } from "~/modules/log/models/AsyncLog.interface";
import logsService from "~/modules/log/services/logs.service";

interface UseFetchLogsResponse extends UseFetchResponse<AsyncLog> {
  searchParams: Ref<string | FiltersSelection>;
}

export function useFetchLogs({
  businessUnitCode,
  organizationCode,
}: UseFetchParameters): UseFetchLogsResponse {
  const appliedFilters: FiltersSelection = new Map([
    [Filters.LOG_ACTION, { key: "action", operator: "=", value: [] }],
    [Filters.LOG_ACTION_TYPE, { key: "actionType", operator: "=", value: [] }],
    [
      Filters.LOG_COMPONENT_ID,
      { key: "componentId", operator: "=", value: "" },
    ],
    [Filters.LOG_END_DATE, { key: "endDate", operator: "=", value: "" }],
    [Filters.LOG_EVENT_TYPE, { key: "eventType", operator: "=", value: [] }],
    [
      Filters.LOG_AUTHOR_CLASS,
      { key: "authorClass", operator: "=", value: "" },
    ],
    [Filters.LOG_AUTHOR_ID, { key: "authorId", operator: "=", value: [] }],
    [Filters.LOG_LEVEL, { key: "level", operator: "=", value: [] }],
    [Filters.LOG_ORIGIN, { key: "origin", operator: "=", value: [] }],
    [Filters.LOG_START_DATE, { key: "startDate", operator: "=", value: "" }],
    [Filters.LOG_TYPE, { key: "type", operator: "=", value: "" }],
    [Filters.STATUS, { key: "status", operator: "=", value: [] }],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.FILTERS_SELECTION,
    appliedFilters,
    SearchMode.URL_SEARCH,
  );

  let service = logsService.getSuperAdministrationLogs.bind(logsService);
  if (businessUnitCode) {
    service = logsService.getBusinessUnitLogs.bind(logsService);
  } else if (organizationCode) {
    service = logsService.getOrganizationLogs.bind(logsService);
  }

  const { sort } = useSort();

  const { isLoading, results, error, fetchData } = useFetchPage({
    businessUnitCode,
    organizationCode,
    service,
    searchParams,
    sort,
    useQueryParametersForPagination: true,
  });

  return {
    error,
    fetchData,
    isLoading,
    results,
    searchParams,
    filters,
    setFilter,
    search,
    onSearch,
  };
}
